import { useEffect, useState, useCallback } from "react";
import { Bell, X, CheckCheck, ExternalLink } from "lucide-react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";

type NotifType =
  | "document" | "actualite" | "ticket" | "devis" | "piece"
  | "robot" | "sous_traitance" | "invitation" | "information";

interface PortailNotif {
  id: string;
  type: NotifType | string;
  titre: string;
  message: string | null;
  lien: string | null;
  lu: boolean;
  createdAt: string;
}

const TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  document:       { label: "Document",     cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  actualite:      { label: "Actualité",    cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  ticket:         { label: "Ticket",       cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  devis:          { label: "Devis",        cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  piece:          { label: "Pièce",        cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  robot:          { label: "Robot",        cls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300" },
  sous_traitance: { label: "Sous-trait.",  cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" },
  invitation:     { label: "Invitation",  cls: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300" },
  information:    { label: "Info",         cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
};

function getTypeBadge(type: string) {
  return TYPE_BADGE[type] ?? { label: "Notification", cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" };
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNotifRead: () => void;
}

export function PortailNotificationsPanel({ isOpen, onClose, onNotifRead }: Props) {
  const { navigate } = usePortailRouter();
  const [items, setItems] = useState<PortailNotif[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    authFetch("/api/portail/notifications")
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => { load(); }, [load]);

  const markLu = async (notif: PortailNotif) => {
    if (!notif.lu) {
      await authFetch(`/api/portail/notifications/${notif.id}/lu`, { method: "PATCH" }).catch(() => {});
      setItems(prev => prev.map(n => n.id === notif.id ? { ...n, lu: true } : n));
      onNotifRead();
    }
    if (notif.lien) {
      navigate(notif.lien);
      onClose();
    }
  };

  const markAllLu = async () => {
    setMarkingAll(true);
    await authFetch("/api/portail/notifications/tout-lu", { method: "POST" }).catch(() => {});
    setItems(prev => prev.map(n => ({ ...n, lu: true })));
    onNotifRead();
    setMarkingAll(false);
  };

  const unread = items.filter(n => !n.lu).length;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay semi-transparent */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panneau latéral droit */}
      <div
        className="fixed top-0 right-0 h-full w-80 z-50 flex flex-col bg-background border-l border-border shadow-xl"
        data-testid="notif-panel"
      >
        {/* En-tête */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-foreground" />
            <span className="font-semibold text-sm text-foreground">Notifications</span>
            {unread > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <button
                onClick={markAllLu}
                disabled={markingAll}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
                title="Tout marquer comme lu"
                data-testid="btn-tout-lu"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout lu
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              data-testid="btn-close-notif-panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 h-48 text-muted-foreground">
              <Bell className="w-8 h-8 opacity-30" />
              <p className="text-sm">Aucune notification pour le moment.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map(notif => {
                const badge = getTypeBadge(notif.type);
                return (
                  <li
                    key={notif.id}
                    onClick={() => markLu(notif)}
                    className={`px-4 py-3 cursor-pointer transition-colors hover:bg-secondary/40 ${
                      notif.lu ? "" : "bg-blue-50/60 dark:bg-blue-950/20"
                    }`}
                    data-testid={`notif-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Pastille non lue */}
                      <div className="mt-1.5 shrink-0 w-2 h-2 rounded-full">
                        {!notif.lu && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {notif.lien && (
                            <ExternalLink className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                          )}
                        </div>
                        <p className={`text-sm leading-snug ${notif.lu ? "text-muted-foreground" : "text-foreground font-medium"}`}>
                          {notif.titre}
                        </p>
                        {notif.message && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                          {relativeTime(notif.createdAt)}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
